import React, { useEffect, useMemo, useState } from 'react'
import { TemplateContainer } from '../style'
import Text from 'particles/Text'
import { useHistoryPushWithForwardedParams, useNewTranslation, useWindowSize } from 'hooks'
import SideNavigator from 'particles/SideNavigator'
import { TFunction } from 'i18next'
import PaymentConfirmation from './PaymentConfirmation'
import { useDispatch, useSelector } from 'react-redux'
import qs from 'query-string'
import {
    clearPaymentSummary,
    clearTemplateData,
    exportTemplate,
    getPaymentSummary,
    getTemplateData,
    getTemplateDetail,
    selectTemplateData,
    submitCampaignTemplate,
} from 'state/campaignTemplates'
import { clearCampaignList } from 'state/campaigns'
import { showSnackBar } from 'services/toast'
import { useHistory } from 'react-router-dom'
import CommentsCenter from 'components/Campaign/CommentsCenter'
import { useIdentifiersMap, useToggleCampaignEditorDrawer } from 'hooks/comment'
import IconButton from 'particles/Button/IconButton'
import TemplateActions from '../TemplateActions'
import Modal from 'particles/Modal'
import Progress from '../TemplateSelector/Progress'
import PaymentCard from './PaymentCard'
import { PaymentIconType } from './PaymentIcon'
import CampaignStatusLabel from '../CampaignStatusLabel'
import { selectCampaignByTemplateData } from 'state'
import Label from 'particles/Label'
import { PaymentListContainer } from './style'
import BankTransfer from './BankTransfer'
import { selectStripeSupportedByEditingCampaignCountry } from 'state/constants'
import { TEMPLATE_TAB_ID } from '..'

const getStepOptions = (t: TFunction) => [
    {
        title: t('campaign.template.step3.paymentConfirmation.title'),
        subtext: t('campaign.template.step3.paymentConfirmation.subText'),
        isCommented: false,
        isCompleted: false,
        isError: false,
        isDisabled: false,
        id: 0,
    },
]

interface TemplatePaymentSummaryProps {
    setSelectTab: React.Dispatch<React.SetStateAction<TEMPLATE_TAB_ID>>
}

const TemplatePaymentSummary: React.FC<TemplatePaymentSummaryProps> = ({ setSelectTab }) => {
    const [selectedSidebar, setSelectedSidebar] = useState(0)
    const [t] = useNewTranslation()
    const stepOptions = getStepOptions(t)
    const dispatch = useDispatch()
    const params = qs.parse(location.search)
    const { templateData: templateDataUuid } = params
    const templateData = useSelector(selectTemplateData)
    const campaign = useSelector(selectCampaignByTemplateData)
    const isStripeSupported = useSelector(selectStripeSupportedByEditingCampaignCountry)
    const [showProgressModal, setShowProgressModal] = useState(false)
    const [campaignSetupProgress, setCampaignSetupProgress] = useState(0)
    const historyReplace = useHistoryPushWithForwardedParams()
    const history = useHistory()
    const [showDrawer, setShowDrawer] = useState<boolean>(false)
    const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false)
    const [showBankInstructionModal, setShowBankInstructionModal] = useState<boolean>(false)
    const defaultPaymentType = isStripeSupported ? 'creditCard' : 'bankTransfer'
    const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentIconType>(defaultPaymentType)
    const searchParams = new URLSearchParams(location.search)
    const currentDrawerStatus = searchParams.get('drawer')
    const isCommentCenterOpened = currentDrawerStatus === 'comments'
    const updateToggleCampaignEditorDrawer = useToggleCampaignEditorDrawer()
    const { unresolvedTemplateFieldIdentifiers } = useIdentifiersMap()
    const windowSize = useWindowSize()

    useEffect(() => {
        // Make sure the drawer is open if commentcenter is open
        if (isCommentCenterOpened) {
            setShowDrawer(true)
        }
    }, [])

    useEffect(() => {
        // Make sure the drawer is open if commentcenter is open and is closed when comment center is not open
        setShowDrawer(isCommentCenterOpened)
    }, [updateToggleCampaignEditorDrawer])

    const updatedStepOptions = useMemo(() => {
        return stepOptions.map(option => {
            const id = option.id === 0 ? 'payment_confirmation' : null
            return {
                ...option,
                isCommented: id ? id in unresolvedTemplateFieldIdentifiers : false,
            }
        })
    }, [stepOptions, unresolvedTemplateFieldIdentifiers])

    useEffect(() => {
        if (templateDataUuid && typeof templateDataUuid === 'string') {
            dispatch(
                getTemplateData({
                    templateDataUuid,
                }),
            )

            dispatch(
                getPaymentSummary({
                    templateUuid: templateDataUuid,
                }),
            )
        }

        return () => {
            dispatch(clearTemplateData())
            dispatch(clearPaymentSummary())
        }
    }, [dispatch, templateDataUuid])

    useEffect(() => {
        if (templateData?.template.uuid) {
            dispatch(
                getTemplateDetail({
                    templateUuid: templateData?.template.uuid,
                }),
            )
        }
    }, [dispatch, templateData])

    const onCampaignCreateSuccessCallback = async () => {
        dispatch(clearCampaignList())
        history.replace('/campaigns')
        showSnackBar({
            title: t('successMessage.readyForReview'),
            type: 'positive',
        })
    }

    const onCampaignCreateFailCallback = () => {
        showSnackBar({
            title: t('errorMessage.validation.enterDetailsRedSubtext'),
            type: 'error',
        })
    }

    const updateProgress = (cb: () => void) => {
        const totalDuration = 3000 // 3 seconds
        const updateInterval = 100 // Update every 100 milliseconds
        let progress = 0
        const step = (updateInterval / totalDuration) * 100 // Calculate the step to increment

        const intervalId = setInterval(() => {
            progress += step
            if (progress >= 100) {
                setCampaignSetupProgress(100)
                clearInterval(intervalId)
                cb()
            } else {
                setCampaignSetupProgress(progress)
            }
        }, updateInterval)
    }

    const submitForm = (isExport: boolean) => {
        if (templateDataUuid && typeof templateDataUuid === 'string') {
            if (isExport) {
                setShowProgressModal(true)
                templateData &&
                    dispatch(
                        exportTemplate({
                            templateUuid: templateDataUuid,
                            campaignUuid: templateData?.campaign.uuid,
                            onSuccessCallback: () => {
                                updateProgress(() => {
                                    history.replace(
                                        `/campaign/edit/registration?campaign=${templateData?.campaign.uuid}`,
                                    )
                                    showSnackBar({
                                        title: t('campaign.template.createCampainSuccessfully.title'),
                                        type: 'positive',
                                    })
                                })
                            },
                            onErrorCallback: () => {
                                setShowProgressModal(false)
                            },
                        }),
                    )
            } else {
                dispatch(
                    submitCampaignTemplate({
                        templateUuid: templateDataUuid,
                        onSuccessCallback: onCampaignCreateSuccessCallback,
                        onErrorCallback: onCampaignCreateFailCallback,
                    }),
                )
            }
        }
    }

    const onBack = () => {
        setSelectTab('templateDetail')
        historyReplace(
            `/campaign/template/templateDetail?templateData=${templateDataUuid}&campaign=${templateData?.campaign.uuid}`,
            {},
            { replace: true },
        )
    }

    const onConfirmPaymentMethod = () => {
        if (selectedPaymentType === 'creditCard') {
            if (campaign?.stripe_payment_link_url) {
                window.location.href = campaign?.stripe_payment_link_url
            }
        } else {
            // display bank transfer modal
            setShowPaymentModal(false)
            setSelectedPaymentType(defaultPaymentType)
            setShowBankInstructionModal(true)
        }
    }

    return (
        <TemplateContainer showDrawer={showDrawer}>
            <div className="top-block">
                <div className="content-block">
                    <Text scale="H3" color="primary">
                        {t('campaign.template.step3.title')}
                    </Text>
                    {campaign && <CampaignStatusLabel status={campaign.status} />}
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                    <IconButton
                        form="outlined"
                        size="s"
                        color="neutral"
                        icon="chatText"
                        iconForm="regular"
                        onClick={() => {
                            setShowDrawer(!showDrawer)
                            updateToggleCampaignEditorDrawer('comments')
                        }}
                        isActivated={isCommentCenterOpened}
                        type="button"
                    />
                </div>
            </div>
            <div className="nav-bar-content">
                <div className="small-sidebar-content">
                    <div className="open-small-sidebar">
                        <Text scale="bodyS500" color="primary">
                            {t('campaignEditor.createTask.currentPageMobilePageTitle')}
                        </Text>

                        <div className="current-page-open-button" onClick={(): void => {}}>
                            <Text scale="bodyS400" color="primary">
                                {updatedStepOptions[selectedSidebar].title}
                            </Text>
                        </div>
                    </div>
                </div>
                <SideNavigator
                    stepOptions={updatedStepOptions}
                    selected={selectedSidebar}
                    onSelectStep={setSelectedSidebar}
                    isDisabledClickable={true}
                    className="side-navigator"
                    isExpanded={windowSize <= 1024 ? !showDrawer : true}
                />
                <div className="sidebar-divider" />
                <div className="form-content">
                    <div className="form-fields">
                        <PaymentConfirmation
                            onBack={onBack}
                            onSubmit={submitForm}
                            setShowPaymentModal={setShowPaymentModal}
                        />
                    </div>

                    {typeof templateDataUuid === 'string' && <TemplateActions templateUuid={templateDataUuid} />}
                </div>
                {showDrawer && <div className="sidebar-divider" />}

                <div className={`right-block ${showDrawer ? 'show' : ''}`}>
                    <div className="right-block-inner-container">
                        {isCommentCenterOpened && <CommentsCenter className="comments-center" />}
                    </div>
                </div>
            </div>
            {showProgressModal && (
                <Modal
                    title={t('campaign.template.contentTransferModal.title')}
                    subtext={t('campaign.template.contentTransferModal.subtext')}
                    size={'l'}
                    hasButtonSet={false}
                    hasIcon={false}
                    isClosable={false}
                >
                    <Progress count={campaignSetupProgress} total={100} />
                </Modal>
            )}
            {showPaymentModal && (
                <Modal
                    title={t('breadcrumb.campaignEditor.campaignPayment')}
                    size={'m'}
                    hasButtonSet
                    hasIcon={false}
                    hasSecondaryButton
                    isClosable
                    onConfirm={onConfirmPaymentMethod}
                    onClose={() => {
                        setShowPaymentModal(false)
                        setSelectedPaymentType(defaultPaymentType)
                    }}
                    primaryButtonProps={{
                        label: t('button.confirm'),
                    }}
                    secondaryButtonProps={{
                        label: t('helper.uploadProgress.inProgress.button.cta1'),
                    }}
                >
                    <PaymentListContainer>
                        <Label
                            title={t('modalWindow.campaignPayment.paymentMethod.title')}
                            subtext={t('modalWindow.campaignPayment.paymentMethod.subtext')}
                            isRequired
                        />
                        <div className="payment-list">
                            {isStripeSupported && (
                                <PaymentCard
                                    title={t('modalWindow.campaignPayment.paymentMethod.creditCard.title')}
                                    description={t('modalWindow.campaignPayment.paymentMethod.creditCard.subtext')}
                                    icon={'creditCard'}
                                    isSelected={selectedPaymentType === 'creditCard'}
                                    isRecommended
                                    onClick={() => {
                                        setSelectedPaymentType('creditCard')
                                    }}
                                    dataTestId="test-credit-card-button"
                                />
                            )}

                            <PaymentCard
                                title={t('modalWindow.campaignPayment.paymentMethod.bankTransfer.title')}
                                description={t('modalWindow.campaignPayment.paymentMethod.bankTransfer.subtext')}
                                icon={'bankTransfer'}
                                isSelected={selectedPaymentType === 'bankTransfer'}
                                onClick={() => {
                                    setSelectedPaymentType('bankTransfer')
                                }}
                            />
                        </div>
                    </PaymentListContainer>
                </Modal>
            )}
            {showBankInstructionModal && (
                <Modal
                    title={t('modalWindow.bankTransferInstructionTitle')}
                    size={'m'}
                    hasButtonSet
                    isClosable
                    hasSecondaryButton={false}
                    hasIcon={false}
                    onClose={() => {
                        setShowBankInstructionModal(false)
                    }}
                    onConfirm={() => {
                        setShowBankInstructionModal(false)
                    }}
                    primaryButtonProps={{
                        label: t('button.ok'),
                    }}
                >
                    <BankTransfer />
                </Modal>
            )}
        </TemplateContainer>
    )
}

export default TemplatePaymentSummary
